import { ENDORSEMENT_TYPES, apiKeyFor } from './endorsementTypes.js';
import { toAttachmentList } from './attachments.js';

/**
 * credentialsPayload — Symfony responses reshaped into the payload the
 * sections already read.
 *
 * The three section components were written against the twig `{% block JSON %}`
 * shape (`documents`, `certificateTypes`, `faaReview`, …). Rather than rewrite
 * every component for the API's shape, the difference is absorbed here: one
 * file to read when a field looks wrong, and one file to delete when the twig
 * routes finally go away.
 *
 * Where the two disagree, the API's shape is richer and the adapter narrows it:
 *   - documents/certificates carry a nested `type` object; the tables key off
 *     the id, so `type` becomes that id and the catalogs supply the name;
 *   - certificate ratings arrive as relation rows
 *     (`{id: relId, rating: {...}, privilege: {...}}`) — the dialog works in
 *     rating ids, so both are kept: `id` is the RATING id, `relId` the row to
 *     delete;
 *   - scans are read from the presigned `attachments[]` / an endorsement's
 *     `attachmentDetails` (`attachments.js`), never from the bare storage
 *     paths in `images[]`/`files[]`.
 */
const str = v => (v == null ? '' : String(v));

// ─── Documents ───────────────────────────────────────────────────────────────

export function toDocumentsPayload(documents, documentTypes) {
	return {
		documents: (documents || []).map(d => ({
			...d,
			type: str(d.type && d.type.id),
			imagesAmount: (d.images || []).length,
			filesAmount: (d.files || []).length
		})),
		documentTypes: (documentTypes || []).map(t => ({ id: str(t.id), name: t.name || str(t.id) }))
	};
}

// ─── Certificates ────────────────────────────────────────────────────────────

export function toCertificatesPayload(certificates, metadata) {
	const meta = metadata || {};
	return {
		certificates: (certificates || []).map(c => ({
			...c,
			type: str(c.type && c.type.id),
			grade: str(c.grade && c.grade.id),
			ratings: (c.ratings || []).map(rel => ({
				id: str(rel.rating && rel.rating.id),
				name: (rel.rating && rel.rating.name) || '',
				relId: str(rel.id),
				privilegeId: str(rel.privilege && rel.privilege.id)
			}))
		})),
		certificateTypes: (meta.types || []).map(t => ({ id: str(t.id), name: t.name || str(t.id) })),
		certificatesGrades: (meta.grades || []).map(g => ({
			id: str(g.id),
			name: g.name || str(g.id),
			certificateTypeID: str(g.certificateType && g.certificateType.id),
			expires: g.expires,
			position: g.position
		})),
		ratings: (meta.ratings || []).map(r => ({
			id: str(r.id),
			name: r.name || str(r.id),
			certificateTypeID: str(r.certificateType && r.certificateType.id),
			certificateRatingType: str(r.certificateRatingCategory && r.certificateRatingCategory.id)
		})),
		ratingsTypes: (meta.ratingCategories || []).map(c => ({
			id: str(c.id),
			name: c.name || str(c.id)
		}))
	};
}

// ─── Endorsements + flight review ────────────────────────────────────────────

/** The FAA row out of the flight-review list; org reviews are shown elsewhere. */
export function pickFaaReview(reviews) {
	const list = Array.isArray(reviews) ? reviews : [];
	const faa = list.find(r => String(r.type).toUpperCase() === 'FAA' || r.organizationId == null);
	return faa || null;
}

export function toEndorsementsPayload(endorsement, flightReviews) {
	const e = endorsement || {};
	const payload = { endorsementId: e.id != null ? e.id : null };

	ENDORSEMENT_TYPES.forEach(t => {
		// One scan per endorsement type, as a presigned `attachmentDetails`.
		payload[t.key] = { attachments: toAttachmentList(e[apiKeyFor(t.key)]) };
	});

	payload.endorsements = ENDORSEMENT_TYPES.filter(t => {
		const detail = e[apiKeyFor(t.key)];
		return !!(detail && detail.enabled);
	}).map(t => t.key);

	const faa = pickFaaReview(flightReviews);
	payload.faaReview = faa
		? {
				id: faa.id,
				date: faa.date,
				expirationDate: faa.expirationDate,
				attachments: toAttachmentList(faa)
			}
		: {};

	return payload;
}

/** The flags `PUT /endorsement` expects, from our ticked-key list. */
export function toEndorsementFlags(tickedKeys) {
	const ticked = Array.isArray(tickedKeys) ? tickedKeys : [];
	const flags = {};
	ENDORSEMENT_TYPES.forEach(t => {
		flags[apiKeyFor(t.key)] = ticked.includes(t.key);
	});
	return flags;
}
